import React, { useState, useEffect, useCallback } from 'react';
import { getExpiringHealthInsurance, requestHealthInsuranceRenewal, cancelHealthInsuranceRenewal } from '../../model/api';
import LoadingSpinner from './common/LoadingSpinner';
import { toast } from 'react-toastify';

const ExpiringPoliciesTab = () => {
    const [expiringPolicies, setExpiringPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);

    const fetchExpiringPolicies = useCallback(async () => {
        setLoading(true);
        try {
            const result = await getExpiringHealthInsurance(30); // 30 days
            if (result.ok) {
                setExpiringPolicies(result.data || []);
            } else {
                setExpiringPolicies([]);
                toast.error('Failed to fetch expiring policies');
            }
        } catch (error) {
            console.error('Error fetching expiring policies:', error);
            toast.error('Error loading expiring policies');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchExpiringPolicies();
    }, [fetchExpiringPolicies]);

    const handleRequestRenewal = async (policyId) => {
        setProcessingId(policyId);
        try {
            const result = await requestHealthInsuranceRenewal(policyId);
            if (result.ok) {
                toast.success('Renewal request submitted successfully');
                fetchExpiringPolicies(); // Refresh list
            } else {
                toast.error(result.error || 'Failed to request renewal');
            }
        } catch (error) {
            console.error('Error requesting renewal:', error);
            toast.error('Error submitting renewal request');
        } finally {
            setProcessingId(null);
        }
    };

    const handleCancelRenewal = async (policyId) => {
        if (!window.confirm('Are you sure you want to cancel this renewal request?')) return;

        setProcessingId(policyId);
        try {
            const result = await cancelHealthInsuranceRenewal(policyId);
            if (result.ok) {
                toast.success('Renewal request cancelled');
                fetchExpiringPolicies(); // Refresh list
            } else {
                toast.error(result.error || 'Failed to cancel renewal');
            }
        } catch (error) {
            console.error('Error cancelling renewal:', error);
            toast.error('Error cancelling renewal request');
        } finally {
            setProcessingId(null);
        }
    };

    const getDaysUntilExpiry = (expiryDate) => {
        const today = new Date();
        const expiry = new Date(expiryDate);
        const diffTime = expiry - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const getExpiryColor = (days) => {
        if (days <= 7) return 'bg-red-100 text-red-800';
        if (days <= 15) return 'bg-orange-100 text-orange-800';
        return 'bg-yellow-100 text-yellow-800';
    };

    return (
        <div className="space-y-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Policies Expiring Soon</h3>
                        <p className="text-sm text-gray-600 mt-1">Policies expiring within 30 days</p>
                    </div>
                    <button
                        onClick={fetchExpiringPolicies}
                        className="text-indigo-600 hover:text-indigo-800 font-medium text-sm flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="p-8"><LoadingSpinner /></div>
            ) : expiringPolicies.length === 0 ? (
                <div className="bg-white p-12 text-center rounded-lg shadow">
                    <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <p className="text-gray-600 font-medium">No policies expiring in the next 30 days</p>
                    <p className="text-gray-500 text-sm mt-2">All your policies are up to date</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Policy No</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Premium</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Left</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {expiringPolicies.map((policy) => {
                                    const daysLeft = getDaysUntilExpiry(policy.policy_expires_date);
                                    const isPending = policy.renewal_status === 'pending_renewal';

                                    return (
                                        <tr key={policy._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{policy.policy_number}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div>{policy.customer_name}</div>
                                                <div className="text-xs text-gray-400">{policy.customer_phone}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{policy.policy_type}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{policy.premium_amount?.toLocaleString('en-IN')}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(policy.policy_expires_date).toLocaleDateString('en-IN')}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getExpiryColor(daysLeft)}`}>
                                                    {daysLeft} {daysLeft === 1 ? 'day' : 'days'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {isPending ? (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                        Renewal Pending
                                                    </span>
                                                ) : (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                                        Not Requested
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <div className="flex gap-2">
                                                    {isPending ? (
                                                        <button
                                                            onClick={() => handleCancelRenewal(policy._id)}
                                                            disabled={processingId === policy._id}
                                                            className="text-red-600 hover:text-red-900 font-medium disabled:opacity-50"
                                                        >
                                                            {processingId === policy._id ? 'Cancelling...' : 'Cancel Review'}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleRequestRenewal(policy._id)}
                                                            disabled={processingId === policy._id}
                                                            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 font-medium"
                                                        >
                                                            {processingId === policy._id ? 'Requesting...' : 'Request Renewal'}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpiringPoliciesTab;
